import i18n from '@yandex-int/i18n';
import { markers } from 'client/model/common';
import { Channel } from 'client/model/skill';

import * as keyset from '../../../i18n/SkillCreation';
import { isEmptyExample } from '../../model/common';
import { AliceSkillBackendSettings, AliceSkillPublishingSettings, SkillAccess } from '../../model/settings';
import { AliceSkillSettings } from '../../model/settings';
import { AliceSkillFormSettings } from '../../types';
import { isPrivateSkillsEnabled } from '..';

import { getParameterWithDefault } from './common';

const t = i18n(keyset);

export const getDefaultSkillName = () => t('alice-skill-default-name');

const getInitialValues = () => {
    const defaultSkillName = getDefaultSkillName();
    return {
        activationPhrases: defaultSkillName,
        brandVerificationWebsite: '',
        logoId: '',
        logoUrl: '',
        name: defaultSkillName,
        voice: 'good_oksana',
        developerName: '',
        category: '',
        explicitContent: false,
        noteForModerator: '',
        description: '',
        backendType: 'webhook',
        uri: '',
        functionId: '',
        structuredExamples: [],
        email: '',
        requiredInterfaces: [],
        exactSurfaces: [],
        surfaceWhitelist: [],
        surfaceBlacklist: [],
        yaCloudGrant: false,
        oauthAppId: '',
        appMetricaApiKey: '',
        useStateStorage: false,
        rsyPlatformId: '',
        skillAccess: SkillAccess.Public,
        hideInStore: false,
        skillUserAgreements: [],
    };
};

export function mapValuesToSkillSettings(values: AliceSkillFormSettings): Partial<AliceSkillSettings> {
    return {
        logo2: null, // TODO separate readable and readonly settings

        name: values.name,
        voice: values.voice,
        activationPhrases: values.activationPhrases,
        logoId: values.logoId,
        noteForModerator: values.noteForModerator,
        yaCloudGrant: values.backendType === 'webhook' ? values.yaCloudGrant : false,

        backendSettings: {
            uri: values.backendType === 'webhook' ? values.uri : '',
            functionId: values.backendType === 'function' ? values.functionId : '',
            backendType: values.backendType,
        },

        publishingSettings: {
            brandVerificationWebsite: values.brandVerificationWebsite,
            category: values.category,
            developerName: values.developerName,
            explicitContent: values.explicitContent,
            structuredExamples: values.structuredExamples.filter((example) => !isEmptyExample(example)),
            description: values.description,
            email: values.email,
        },

        requiredInterfaces: values.requiredInterfaces,
        exactSurfaces: values.exactSurfaces,
        surfaceWhitelist: values.surfaceWhitelist,
        surfaceBlacklist: values.surfaceBlacklist,
        oauthAppId: values.oauthAppId,
        appMetricaApiKey: values.appMetricaApiKey,
        useStateStorage: values.useStateStorage,
        rsyPlatformId: values.rsyPlatformId,
        skillAccess: values.skillAccess,
        hideInStore: values.hideInStore,
    };
}

export function mapSkillSettingsToValues(skillSettings: AliceSkillSettings): AliceSkillFormSettings {
    const initialValues = getInitialValues();
    const {
        brandVerificationWebsite,
        category,
        developerName,
        explicitContent,
        description,
        structuredExamples,
        email,
    } = { ...initialValues, ...skillSettings.publishingSettings } as Required<AliceSkillPublishingSettings>;

    const { uri, functionId, backendType } = {
        ...initialValues,
        ...skillSettings.backendSettings,
    } as Required<AliceSkillBackendSettings>;

    const activationPhrases =
        skillSettings.activationPhrases && skillSettings.activationPhrases.filter(Boolean).length > 0
            ? skillSettings.activationPhrases
            : [skillSettings.name];

    const displaySkillAccess = isPrivateSkillsEnabled();

    return {
        brandVerificationWebsite,
        developerName,
        category,
        explicitContent,
        description,
        structuredExamples,
        uri,
        email,
        backendType,
        functionId,

        yaCloudGrant: getParameterWithDefault(skillSettings, initialValues, 'yaCloudGrant'),
        name: getParameterWithDefault(skillSettings, initialValues, 'name'),
        logoId: getParameterWithDefault(skillSettings, initialValues, 'logoId'),
        logoUrl: skillSettings?.logo2?.url || '',
        activationPhrases,
        noteForModerator: getParameterWithDefault(skillSettings, initialValues, 'noteForModerator'),
        voice: getParameterWithDefault(skillSettings, initialValues, 'voice'),

        requiredInterfaces: getParameterWithDefault(skillSettings, initialValues, 'requiredInterfaces'),
        exactSurfaces: getParameterWithDefault(skillSettings, initialValues, 'exactSurfaces'),
        surfaceWhitelist: getParameterWithDefault(skillSettings, initialValues, 'surfaceWhitelist'),
        surfaceBlacklist: getParameterWithDefault(skillSettings, initialValues, 'surfaceBlacklist'),

        oauthAppId: getParameterWithDefault(skillSettings, initialValues, 'oauthAppId'),
        appMetricaApiKey: getParameterWithDefault(skillSettings, initialValues, 'appMetricaApiKey'),
        useStateStorage: getParameterWithDefault(skillSettings, initialValues, 'useStateStorage'),
        rsyPlatformId: getParameterWithDefault(skillSettings, initialValues, 'rsyPlatformId'),
        skillAccess: displaySkillAccess
            ? getParameterWithDefault(skillSettings, initialValues, 'skillAccess')
            : undefined,
        hideInStore: getParameterWithDefault(skillSettings, initialValues, 'hideInStore'),
        skillUserAgreements: getParameterWithDefault(skillSettings, initialValues, 'skillUserAgreements'),
    };
}

export const mapChannelToDefaultName: Record<Channel, string> = {
    [Channel.AliceSkill]: t('new-skill'),
    [Channel.AutoApp]: t('new-app'),
    [Channel.SmartHome]: t('new-smart-home'),
    [Channel.TvApp]: t('new-app'),
    [Channel.Thereminvox]: t('new-skill'),
    [Channel.SmartScreenApp]: t('new-app'),
};

export function shouldDisplayRequest(checkedMarker: string) {
    const marker = markers.find((a) => a.text === checkedMarker);

    return marker !== undefined && marker.hasRequest;
}
